/**
 * Endpoints de **Logística → Pagos Adelantados**
 * (`/api/logistica/pagos-adelantados`).
 *
 * El listado va paginado en el servidor (los contadores del histórico completo
 * vienen en la misma respuesta) y la anulación se pide por pago.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type { PagosAdelantadosFiltros, PagosAdelantadosRespuesta } from './pagos-adelantados.types';

export const pagosAdelantadosKeys = {
  lista: (filtros: PagosAdelantadosFiltros) =>
    ['logistica', 'pagos-adelantados', filtros] as const,
};

/** Arma la cadena de consulta del listado. */
function cadenaDe(filtros: PagosAdelantadosFiltros): string {
  const parametros = new URLSearchParams();

  if (filtros.search) parametros.set('search', filtros.search);
  if (filtros.page) parametros.set('page', String(filtros.page));
  if (filtros.perPage) parametros.set('per_page', String(filtros.perPage));

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const pagosAdelantadosApi = {
  listar: (
    filtros: PagosAdelantadosFiltros = {},
    signal?: AbortSignal,
  ): Promise<PagosAdelantadosRespuesta> =>
    apiClient.get<PagosAdelantadosRespuesta>(
      `/api/logistica/pagos-adelantados${cadenaDe(filtros)}`,
      { signal },
    ),

  anular: (id: number): Promise<{ message: string; id: number }> =>
    apiClient.put<{ message: string; id: number }>(
      `/api/logistica/pagos-adelantados/${id}/anular`,
    ),
};

/** Pagos adelantados del filtro pedido. */
export function usePagosAdelantados(filtros: PagosAdelantadosFiltros = {}) {
  return useQuery({
    queryKey: pagosAdelantadosKeys.lista(filtros),
    queryFn: ({ signal }) => pagosAdelantadosApi.listar(filtros, signal),
  });
}

/** Anula un pago adelantado y refresca el listado. */
export function useAnularPagoAdelantado() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => pagosAdelantadosApi.anular(id),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['logistica', 'pagos-adelantados'] });
    },
  });
}
